import type { SearchLocation } from '../model/search-location';

export const getLocationCity = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value : undefined;

export const cleanLocationCity = <T extends SearchLocation>(location: T): T => {
    const { city, ...rest } = location;
    const validCity = getLocationCity(city);
    return { ...rest, ...(validCity !== undefined ? { city: validCity } : {}) } as T;
};

const reliableId = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isSafeInteger(value)) return String(value);
    return undefined;
};

type LocationIdentity = { id?: string | number; type?: string };

export const isSameLocationIdentity = (
    a: LocationIdentity | undefined | null,
    b: LocationIdentity | undefined | null
): boolean => {
    const id = reliableId(a?.id);
    return (
        id !== undefined &&
        id === reliableId(b?.id) &&
        typeof a?.type === 'string' &&
        !!a.type.trim() &&
        a.type === b?.type
    );
};

// History can preserve a known city, but never change the current selection's source.
export const mergeLocationCity = <T extends SearchLocation>(
    incoming: T,
    candidates: readonly SearchLocation[]
): T => {
    const clean = cleanLocationCity(incoming);
    if (clean.city !== undefined) return clean;
    const cities = new Set(
        candidates
            .filter((candidate) => isSameLocationIdentity(clean, candidate))
            .map((candidate) => getLocationCity(candidate.city))
            .filter((city): city is string => city !== undefined)
    );
    return cities.size === 1 ? { ...clean, city: [...cities][0] } : clean;
};
